/***
 * Created on 2025/6/11
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React from 'react';
import ModalComponent from "../common/ModalComonent/ModalComponent";
import status_checked_svg from '../../static/img/utils/ic-status-checked.svg';
import group_icon_svg from '../../static/img/utils/ic-avatar-group.svg';
import UserAvatarIcon from "../common/UserAvatarIcon";
import UserStatusComponent from "../common/UserStatusComponent";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {buildStandaloneAdminPayload} from "./siteAdminUtils";
import {useOrganization} from "../../context/OrganizationContext";
import useToggle from "../../hook/ToggleHook";
import {invalidateAccessGrant} from "../../service/accessGrantSync";

export const filterEligibleUsers = (users, searchText) => {
    const needle = (searchText || '').toLowerCase();
    return (users || [])
        .filter(u => !(u?.is_owner || u?.role === 'owner'))
        .filter(u => {
            if (!needle) return true;
            const name = (u?.display_name || u?.name || '').toLowerCase();
            const email = (u?.email || '').toLowerCase();
            return name.includes(needle) || email.includes(needle);
        });
};

export const filterGroupsBySearch = (groups, searchText) => {
    const needle = (searchText || '').toLowerCase();
    return (groups || []).filter(g => {
        if (!needle) return true;
        return (g?.name || '').toLowerCase().includes(needle);
    });
};

function UserAdminListItem({user, admins, t, setAdmins}) {
    const isEligible = user?.status === 'active';

    const onClick = () => {
        if (!isEligible) return;
        if (!admins) {
            setAdmins({users: [user.id], groups: []});
        }
        else if (admins?.users?.includes(user.id)) {
            setAdmins({...admins, users: admins?.users.filter(id => id !== user.id)});
        } else {
            setAdmins({...admins, users: [...admins?.users, user.id]});
        }
    };

    const checked = admins?.users?.includes(user.id) || false;

    return (
        <li className={`list-item list-item--cb ${checked ? 'selected' : ''}`}>
            <div className="contact-info">
                <div className="comp-checkbox no-label">
                    <input type="checkbox" id={user.id}
                           checked={checked}
                           disabled={!isEligible}
                           onChange={onClick}/>
                    <label htmlFor={user.id} className="comp-label"></label>
                </div>
                <UserAvatarIcon user_info={user} with_user_info={true} avatar_class="account-img"/>
                <div className="account-status">
                    <UserStatusComponent user={user}/>
                </div>
            </div>
        </li>
    );
}

const GroupAdminListItem = ({group, admins, t, setAdmins}) => {
    console.log('GroupAdminListItem', group, admins);
    const onClick = () => {
        if (!admins) {
            setAdmins({users: [], groups: [group.organization_group_id]});
        }
        else if (admins?.groups?.includes(group.organization_group_id)) {
            setAdmins({...admins, groups: admins?.groups.filter(id => id !== group.organization_group_id)});
        } else {
            setAdmins({...admins, groups: [...admins.groups, group.organization_group_id]});
        }
    }

    return (
        <li className={`list-item list-item--cb ${admins?.groups?.includes(group.organization_group_id) ? 'selected' : ''}`}>
            <div className="contact-info">
                <div className="comp-checkbox no-label">
                    <input type="checkbox" id={group.organization_group_id} checked={admins?.groups?.includes(group.organization_group_id)} onChange={onClick}/>
                    <label htmlFor={group.organization_group_id} className="comp-label"></label>
                </div>
                <div className="account-img">
                    <div className="avatar"><img src={group_icon_svg} /></div>
                </div>
                <div className="account-info">
                    <div className="name">{group.name}</div>
                </div>
                <div className="select-check">
                    <img src={status_checked_svg} alt=""/>
                </div>
            </div>
        </li>
    );
}

function SiteAdminStandaloneModalComponent({dispatch, isOpen, t, state}) {
    const {userProfile} = useOrganization();
    const {is_owner} = userProfile();
    const [admins, setAdmins] = React.useState({
        users: state?.row_data?.admin?.users || [],
        groups: state?.row_data?.admin?.groups || [],
    });
    const [searchText, setSearchText] = React.useState('');
    const [adminType, setAdminType] = React.useState('qid');
    const [open, node, _context] = useToggle();
    const {OrganizationAPI} = useOrganizationApi();
    const queryClient = useQueryClient();

    const onClose = () => {
        dispatch({type: 'toggle_standalone_admin', payload: false});
    }

    const onSearchChange = (e) => {
        setSearchText(e.target.value);
    }

    const updateAdminMutation = useMutation({
        mutationFn: async (data) => await OrganizationAPI.put(`organization/${state?.row_data?.organization_id}/site/${state?.row_data?.organization_site_id}`, {}, data),
        onSuccess: (data) => {
            invalidateAccessGrant(queryClient, state?.row_data?.organization_id);
            dispatch({'type': 'refresh'});
        },
        onError: (error) => {}
    });

    if (!is_owner) return null;

    const onSave = async () => {
        const data = buildStandaloneAdminPayload(admins, state?.row_data, state?.user_map, state?.group_map);
        try {
            await updateAdminMutation.mutateAsync(data);
            dispatch({type: 'toggle_standalone_admin', payload: false});
        } catch (error) {
            console.error('Failed to update admin:', error);
        }
    }

    return (
        <ModalComponent isOpen={true} title={t('@Add Member as Site Admin@')} onClose={onClose} onSubmit={onSave} okText={t('@Save@')} >
            <div className="dialog-content">
                <div className="comp-contacts">
                    <div className="comp-select mb-16">
                        <div className="select-toggle" ref={node}>
                            <span className="select-label">
                                {adminType === 'qid' ? 'QID' : t('@Group@')}
                            </span>
                        </div>
                        <div className={`comp-dropdown comp-select-dropdown ${open === 'show' ? 'active' : 'hide'}`}>
                            <ul className="comp-dropdown-menu">
                                <li className="section">
                                    <div className="section-items selection" onClick={() => setAdminType('qid')}>QID</div>
                                    <div className="section-items selection" onClick={() => setAdminType('group')}>{t('@Group@')}</div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="filter-list-group filter-list-group--clean">
                        <div className="filter">
                            <div className="filter-bar">
                                <input type="text" placeholder={t('@Search@')} onChange={onSearchChange} value={searchText}/>
                            </div>
                        </div>
                        <ul className="list overlay-scroll">
                            {adminType === 'qid' && filterEligibleUsers(Object.values(state?.user_map || {}), searchText).map((user) => (
                                <UserAdminListItem
                                    key={user.id}
                                    user={user}
                                    t={t}
                                    admins={admins}
                                    setAdmins={setAdmins}
                                />
                            ))}
                            {adminType === 'group' && filterGroupsBySearch(Object.values(state?.group_map || {}), searchText).map((group) => (
                                <GroupAdminListItem
                                    key={group.organization_group_id}
                                    group={group}
                                    t={t}
                                    admins={admins}
                                    setAdmins={setAdmins}
                                />
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </ModalComponent>
    );
}

export default SiteAdminStandaloneModalComponent;
